import { ReactNode, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'radix-ui';
import { withStyles } from 'vopay';
import styles from './TruncatedText.module.scss';

const cn = withStyles(styles);

type TruncatedTextProps = {
  /** Clipped with an ellipsis when it outgrows the width it is given. */
  children: ReactNode;
  /** What the tooltip shows in place of `children`, for a summary standing in for a longer list. */
  information?: ReactNode;
  /** Cap on the tooltip's width. The content wraps within it, however unbroken the value is. */
  width?: string;
  className?: string;
};

/**
 * Text that clips to one line and offers the rest on hover or focus, but only once it actually clips —
 * a tooltip on a value that already fits is noise. Unlike `Truncate`, which cuts at a character count,
 * the clipping here is the browser's own, so it follows the real column width. Built on Radix's
 * tooltip with the text itself as the trigger (`asChild`), so it adds no button of its own and can sit
 * inside a link or a button without nesting interactive elements.
 */
export default function TruncatedText({ children, information, width = '280px', className }: TruncatedTextProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isClipped, setIsClipped] = useState(false);
  // Controlled from the first render: letting `open` flip between a value and undefined as the
  // observer reports clipping would switch the tooltip between controlled and uncontrolled.
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const element = textRef.current;

    if (!element) return undefined;

    // Observed rather than measured once: the same string clips in the account page's 105px tile and
    // fits in the drawer's wider one, and those widths move with the viewport.
    const measure = () => setIsClipped(element.scrollWidth > element.clientWidth);
    const observer = new ResizeObserver(measure);

    measure();
    observer.observe(element);

    return () => observer.disconnect();
  }, [children]);

  // `information` means the tooltip carries something the text itself does not — a summary standing
  // in for a longer list — so it stays reachable whether or not the text clips.
  const hasMoreToShow = isClipped || information !== undefined;

  return (
    <Tooltip.Provider delayDuration={0}>
      {/* Pinned closed while the text fits and says everything; hover / focus drives it otherwise. */}
      <Tooltip.Root open={hasMoreToShow && isOpen} onOpenChange={setIsOpen}>
        <Tooltip.Trigger asChild>
          <span ref={textRef} className={cn('text', className)} tabIndex={hasMoreToShow ? 0 : undefined}>
            {children}
          </span>
        </Tooltip.Trigger>

        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={6}
            className={cn('information')}
            style={{ maxWidth: width }}
            data-testid="truncatedTextTooltip"
          >
            {information ?? children}
            <Tooltip.Arrow className={cn('arrow')} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
